import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { Candidate } from '@/types/models';
import DepartmentDropdown from '@/components/employees/DepartmentDropdown';
import { getJobPostings, JobPosting } from '@/services/job.service';

import { useSharedData } from '@/hooks/useSharedData';
import { Upload, X } from 'lucide-react';

// Zod schema for candidate validation
const candidateSchema = z.object({
  firstName: z.string()
    .min(2, 'First Name must be at least 2 characters')
    .max(50, 'First Name must be less than 50 characters')
    .regex(/^[a-zA-Z\s]+$/, 'First Name should only contain letters and spaces'),
  lastName: z.string()
    .min(2, 'Last Name must be at least 2 characters')
    .max(50, 'Last Name must be less than 50 characters')
    .regex(/^[a-zA-Z\s]+$/, 'Last Name should only contain letters and spaces'),
  email: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
  phone: z.string()
    .optional()
    .or(z.literal(''))
    .refine((val) => {
      if (!val || val === '') return true;
      return /^[+]?[\d\s\-()]{10,15}$/.test(val);
    }, 'Please enter a valid phone number'),
  experienceYears: z.string()
    .min(1, 'Experience is required')
    .refine((val) => {
      const exp = parseFloat(val);
      return !isNaN(exp) && exp >= 0 && exp <= 50;
    }, 'Experience must be a valid number between 0 and 50 years'),
  currentCompany: z.string().max(100, 'Current Company must be less than 100 characters').optional(),
  currentRole: z.string().max(100, 'Current Role must be less than 100 characters').optional(),
  expectedSalary: z.string().optional(),
  currentSalary: z.string().optional(),
  noticePeriod: z.string().max(50, 'Notice Period must be less than 50 characters').optional(),
  department: z.string().min(1, 'Department is required'),
  position: z.string().min(1, 'Job Position is required'),
  linkedinUrl: z.string()
    .optional()
    .refine((val) => {
      if (!val?.trim()) return true;
      const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
      return linkedinRegex.test(val);
    }, 'Please enter a valid LinkedIn profile URL'),

  communicationSkills: z.string().optional().or(z.literal('')),
  source: z.string().optional().or(z.literal('')),
  otherSource: z.string().optional().or(z.literal('')),
  reasonForLeavingPreviousRole: z.string().optional().or(z.literal('')),
  location: z.string().min(1, 'Location is required'),
  notes: z.string().optional().or(z.literal('')),
});

type CandidateFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  experienceYears: string;
  currentCompany: string;
  currentRole: string;
  expectedSalary: string;
  currentSalary: string;
  noticePeriod: string;
  department: string;
  position: string;
  linkedinUrl: string;

  communicationSkills: string;
  source: string;
  otherSource: string;
  reasonForLeavingPreviousRole: string;
  location: string;
  notes: string;
};

interface ValidationErrors {
  [key: string]: string;
}

interface AddCandidateDialogProps {
  onAddCandidate?: (candidate: any) => void;
  onUpdateCandidate?: (id: string, candidate: any) => void;
  candidate?: Candidate | null;
  isEditMode?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AddCandidateDialog({
  onAddCandidate,
  onUpdateCandidate,
  candidate = null,
  isEditMode = false,
  open,
  onOpenChange
}: AddCandidateDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { departments } = useSharedData();

  // Use external open state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  const [form, setForm] = useState<CandidateFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    experienceYears: '',
    currentCompany: '',
    currentRole: '',
    expectedSalary: '',
    currentSalary: '',
    noticePeriod: '',
    department: '',
    position: '',
    linkedinUrl: '',

    communicationSkills: '',
    source: '',
    otherSource: '',
    reasonForLeavingPreviousRole: '',
    location: 'Ahmedabad',
    notes: '',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [jobPositions, setJobPositions] = useState<JobPosting[]>([]);
  const staticSources = [
    { id: 'LinkedIn', name: 'LinkedIn' },
    { id: 'WhatsApp', name: 'WhatsApp' },
    { id: 'Career Site', name: 'Career Site' },
    { id: 'Reference', name: 'Reference' },
    { id: 'Other', name: 'Other' }
  ];
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeFileName, setResumeFileName] = useState<string>('');
  const [existingResumeLink, setExistingResumeLink] = useState<string | null>(null);
  // Note: resumeRemoved is not supported by the backend
  // const [resumeRemoved, setResumeRemoved] = useState<boolean>(false);

  // Fetch job positions
  useEffect(() => {
    const fetchJobPositions = async () => {
      try {
        const response = await getJobPostings();
        const jobsList: JobPosting[] = Array.isArray(response.data) ? response.data : (response.data?.data || []);

        // Only show OPEN jobs, or the current job if we are in edit mode
        const openJobs = jobsList.filter(job => {
          const statusVal = typeof job.status === 'string'
            ? job.status
            : (job.status as any)?.value || (job.status as any)?.name;

          const isOpen = String(statusVal).toUpperCase() === 'OPEN';

          // If editing a candidate, we should still show their current job in the list
          let isCurrentJob = false;
          if (isEditMode && candidate) {
            const candPos = (candidate as any).position;
            const candPosId = typeof candPos === 'object' && candPos ? candPos._id : candPos;
            const candJobId = (candidate as any).jobPosting;
            if (job._id === candPosId || job._id === candJobId) {
              isCurrentJob = true;
            }
          }

          return isOpen || isCurrentJob;
        });

        setJobPositions(openJobs);
      } catch (error) {
        // Error fetching job positions
      }
    };
    fetchJobPositions();
  }, [isEditMode, candidate]);


  // Zod validation function
  const validateField = (name: keyof CandidateFormData, value: string): string | undefined => {
    try {
      const fieldSchema = z.object({ [name]: candidateSchema.shape[name] });
      fieldSchema.parse({ [name]: value });
      return undefined;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldError = error.errors.find(err => err.path[0] === name);
        return fieldError?.message;
      }
      return undefined;
    }
  };

  const validateForm = (): boolean => {
    try {
      candidateSchema.parse(form);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: ValidationErrors = {};
        error.errors.forEach(err => {
          if (err.path[0]) {
            newErrors[err.path[0]] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const resetFormState = () => {
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      experienceYears: '',
      currentCompany: '',
      currentRole: '',
      expectedSalary: '',
      currentSalary: '',
      noticePeriod: '',
      department: '',
      position: '',
      linkedinUrl: '',

      communicationSkills: '',
      reasonForLeavingPreviousRole: '',
      source: 'Direct',
      otherSource: '',
      location: 'Ahmedabad',
      notes: '',
    });
    setErrors({});
    setTouched({});
    setResumeFile(null);
    setResumeFileName('');
    setExistingResumeLink(null);
  };

  // Populate form with candidate data in edit mode
  useEffect(() => {
    // Only wait for departments in edit mode to populate IDs
    if (isEditMode && departments.length === 0) {
      return;
    }

    if (isEditMode && candidate) {
      const departmentId = getDepartmentIdFromCandidate(candidate);

      let positionValue = '';
      if (candidate.position) {
        if (typeof candidate.position === 'string') {
          positionValue = candidate.position;
        }
        else if (typeof candidate.position === 'object' && candidate.position && '_id' in candidate.position) {
          positionValue = (candidate.position as any)._id;
        }
      }
      else if ((candidate as any).jobPosting) {
        positionValue = (candidate as any).jobPosting;
      }



      const sourceValue = candidate.source
        ? (typeof candidate.source === 'string' ? candidate.source : (candidate.source as any).name || (candidate.source as any)._id)
        : '';

      const isStaticSource = staticSources.some(s => s.id === sourceValue);

      const formData: CandidateFormData = {
        firstName: candidate.firstName || '',
        lastName: candidate.lastName || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        experienceYears: candidate.experienceYears?.toString() || '',
        currentCompany: candidate.currentCompany || '',
        currentRole: candidate.currentRole || '',
        expectedSalary: candidate.expectedSalary?.toString() || '',
        currentSalary: (candidate as any).currentSalary?.toString() || '',
        noticePeriod: candidate.noticePeriod || '',
        department: departmentId,
        position: positionValue,
        linkedinUrl: (candidate as any).linkedinUrl || '',

        communicationSkills: (candidate as any).communicationSkills || '',
        source: isStaticSource ? sourceValue : (sourceValue ? 'Other' : ''),
        otherSource: isStaticSource ? '' : sourceValue,
        reasonForLeavingPreviousRole: (candidate as any).reasonForLeavingPreviousRole || '',
        location: (candidate as any).location || 'Ahmedabad',
        notes: (candidate as any).notes || '',
      };

      setForm(formData);
      setErrors({});
      setTouched({ department: true });

      if (candidate.resumeLink) {
        const fileName = (candidate as any).resumeFileName || 'resume.pdf';
        setResumeFileName(fileName);
        setExistingResumeLink(candidate.resumeLink);
        setResumeFile(null); // Do not create a fake 0-byte file
      } else {
        setResumeFile(null);
        setResumeFileName('');
        setExistingResumeLink(null);
      }

      if (departmentId) {
        const departmentError = validateField('department', departmentId);
        if (departmentError) {
          setErrors(prev => ({ ...prev, department: departmentError }));
        }
      }
    }
  }, [isEditMode, candidate, departments, jobPositions]);



  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name as keyof CandidateFormData, value);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof CandidateFormData, value);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const getDepartmentIdFromCandidate = (candidate: Candidate): string => {
    if (!candidate.department) {
      return "";
    }

    if (typeof candidate.department === 'object' && candidate.department._id) {
      return candidate.department._id;
    }

    if (typeof candidate.department === 'string') {
      return candidate.department;
    }

    return "";
  };

  const handleDepartmentChange = (value: string) => {
    setForm({ ...form, department: value });
    setTouched(prev => ({ ...prev, department: true }));
    const error = validateField('department', value);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors.department = error;
      } else {
        delete newErrors.department;
      }
      return newErrors;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
    // Mark resume file as touched for validation
    setTouched(prev => ({ ...prev, resumeFile: true }));
  };

  const validateAndSetFile = (file: File) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        resumeFile: 'Please upload a PDF or Word document'
      }));
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        resumeFile: 'File size must be less than 5MB'
      }));
      return;
    }

    setResumeFile(file);
    setResumeFileName(file.name);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.resumeFile;
      return newErrors;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
    // Mark resume file as touched for validation
    setTouched(prev => ({ ...prev, resumeFile: true }));
  };

  const removeResumeFile = () => {
    setResumeFile(null);
    setResumeFileName('');
    setExistingResumeLink(null);
    // Mark resume file as touched for validation
    setTouched(prev => ({ ...prev, resumeFile: true }));
    // Note: resumeRemoved is not supported by the backend
    // setResumeRemoved(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const allTouched = Object.keys(form).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouched(allTouched);

    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    const departmentId = form.department || null;





    const payload: any = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      currentCompany: form.currentCompany.trim(),
      currentRole: form.currentRole.trim(),
      experienceYears: parseFloat(form.experienceYears),
      expectedSalary: form.expectedSalary.trim(),
      currentSalary: form.currentSalary.trim(),
      noticePeriod: form.noticePeriod.trim(),
      department: departmentId,
      position: (form.position === "none" || !form.position) ? undefined : form.position,
      linkedinUrl: form.linkedinUrl.trim(),

      communicationSkills: form.communicationSkills.trim() || undefined,
      source: form.source === 'Other' ? form.otherSource : form.source,
      reasonForLeavingPreviousRole: form.reasonForLeavingPreviousRole.trim(),
      location: form.location,
      notes: form.notes.trim(),
    };
    
    // Only add resumeFile if it's an actual File selected by the user (not null)
    if (resumeFile) {
      payload.resumeFile = resumeFile;
    }





    try {
      if (isEditMode && candidate && onUpdateCandidate) {
        await onUpdateCandidate(candidate._id, payload);
        setIsOpen(false);
      } else if (!isEditMode && onAddCandidate) {
        await onAddCandidate(payload);
        resetFormState();
        setIsOpen(false);
      }
    } catch (error) {
      // Submission failed, keep form data and keep dialog open
      console.error('Candidate submission failed:', error);
    }
  };

  const isFormValid = () => {
    // Basic required fields (always required)
    const basicRequiredFieldsFilled = form.firstName.trim() &&
      form.lastName.trim() &&
      form.experienceYears.trim() &&
      form.department.trim() &&
      form.position.trim();

    // For new candidates (not edit mode), resume is strictly required
    const isResumePresent = isEditMode || !!resumeFile || !!existingResumeLink;

    const requiredFieldsFilled = !!basicRequiredFieldsFilled && isResumePresent;

    const hasValidationErrors = Object.entries(errors).some(([key, error]) => {
      const fieldValue = form[key as keyof typeof form];
      return error && (touched[key] || (typeof fieldValue === 'string' && fieldValue.trim()));
    });

    return requiredFieldsFilled && !hasValidationErrors;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden rounded-2xl border-2 shadow-lg flex flex-col">
        <DialogTitle className="sr-only">{isEditMode ? 'Edit Candidate' : 'Add New Candidate'}</DialogTitle>
        <DialogDescription className="sr-only">Form to {isEditMode ? 'edit an existing' : 'add a new'} candidate to the hiring system.</DialogDescription>
        <DialogHeader className="rounded-t-2xl bg-background p-6 border-b flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {isEditMode ? 'Edit Candidate' : 'Add New Candidate'}
          </DialogTitle>
          {isEditMode && candidate && (
            <p className="text-sm text-gray-600 mt-1">
              Auto-filling form with existing candidate data...
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
          <form onSubmit={handleSubmit} className="space-y-6 pb-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name <span className="text-red-500">*</span></label>
                <Input
                  name="firstName"
                  placeholder="First Name"
                  value={form.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.firstName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.firstName && touched.firstName && (
                  <p className="text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name <span className="text-red-500">*</span></label>
                <Input
                  name="lastName"
                  placeholder="Last Name"
                  value={form.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.lastName && touched.lastName && (
                  <p className="text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.email && touched.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.phone && touched.phone && (
                  <p className="text-sm text-red-500">{errors.phone}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Experience (Years) <span className="text-red-500">*</span></label>
                <Input
                  name="experienceYears"
                  type="number"
                  placeholder="Experience in years (e.g., 3.5)"
                  value={form.experienceYears}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="0"
                  max="50"
                  step="0.1"
                  className={`${errors.experienceYears ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none cursor-text`}
                />
                {errors.experienceYears && touched.experienceYears && (
                  <p className="text-sm text-red-500">{errors.experienceYears}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Job Position <span className="text-red-500">*</span></label>
                <Select value={form.position || undefined} onValueChange={value => setForm({ ...form, position: value })}>
                  <SelectTrigger className={!form.position.trim() && touched.position ? 'border-red-500 focus:border-red-500' : ''}>
                    <SelectValue placeholder="Select Job Position" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobPositions.map((job) => {
                      const jobStatusText = typeof job.status === 'string'
                        ? job.status
                        : (typeof job.status === 'object' && job.status && 'name' in job.status)
                          ? (job.status as any).name
                          : 'Unknown';

                      return (
                        <SelectItem key={job._id} value={job._id || 'none'}>
                          {job.title} ({jobStatusText})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {/* Position becomes optional/General by default */}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Department <span className="text-red-500">*</span></label>
                <DepartmentDropdown
                  value={form.department || ""}
                  onValueChange={handleDepartmentChange}
                  departments={departments}
                  className={errors.department && touched.department ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.department && touched.department && (
                  <p className="text-sm text-red-500">{errors.department}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Company</label>
                <Input
                  name="currentCompany"
                  placeholder="Current Company"
                  value={form.currentCompany}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.currentCompany ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.currentCompany && touched.currentCompany && (
                  <p className="text-sm text-red-500">{errors.currentCompany}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Role</label>
                <Input
                  name="currentRole"
                  placeholder="Current Role"
                  value={form.currentRole}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.currentRole ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.currentRole && touched.currentRole && (
                  <p className="text-sm text-red-500">{errors.currentRole}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notice Period</label>
                <Input
                  name="noticePeriod"
                  placeholder="e.g., 2 months, Immediate"
                  value={form.noticePeriod}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.noticePeriod ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.noticePeriod && touched.noticePeriod && (
                  <p className="text-sm text-red-500">{errors.noticePeriod}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Salary</label>
                <Input
                  name="currentSalary"
                  type="text"
                  placeholder="e.g., 10lpa, 10 LPA, 10 lakhs"
                  value={form.currentSalary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.currentSalary ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.currentSalary && touched.currentSalary && (
                  <p className="text-sm text-red-500">{errors.currentSalary}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Expected Salary</label>
                <Input
                  name="expectedSalary"
                  type="text"
                  placeholder="e.g., 12lpa, 12 LPA, 12 lakhs"
                  value={form.expectedSalary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.expectedSalary ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.expectedSalary && touched.expectedSalary && (
                  <p className="text-sm text-red-500">{errors.expectedSalary}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Reason for Leaving Previous Role</label>
              <Input
                name="reasonForLeavingPreviousRole"
                placeholder="Briefly explain the reason for leaving"
                value={form.reasonForLeavingPreviousRole}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.reasonForLeavingPreviousRole ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.reasonForLeavingPreviousRole && touched.reasonForLeavingPreviousRole && (
                <p className="text-sm text-red-500">{errors.reasonForLeavingPreviousRole}</p>
              )}
            </div>



            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Communication Skills</label>
                <Select
                  value={form.communicationSkills || undefined}
                  onValueChange={value => setForm({ ...form, communicationSkills: value })}
                >
                  <SelectTrigger className={errors.communicationSkills && touched.communicationSkills ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select Communication Skills" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Poor">Poor</SelectItem>
                    <SelectItem value="Below Average">Below Average</SelectItem>
                    <SelectItem value="Average">Average</SelectItem>
                    <SelectItem value="Good">Good</SelectItem>
                    <SelectItem value="Excellent">Excellent</SelectItem>
                    <SelectItem value="Outstanding">Outstanding</SelectItem>
                  </SelectContent>
                </Select>
              </div>

            </div>



            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">LinkedIn Profile</label>
                <Input
                  name="linkedinUrl"
                  type="url"
                  placeholder="https://linkedin.com/in/username"
                  value={form.linkedinUrl}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.linkedinUrl ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.linkedinUrl && touched.linkedinUrl && (
                  <p className="text-sm text-red-500">{errors.linkedinUrl}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Location <span className="text-red-500">*</span></label>
                <Select
                  value={form.location}
                  onValueChange={value => setForm({ ...form, location: value })}
                >
                  <SelectTrigger className={errors.location && touched.location ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
                    <SelectItem value="Surat">Surat</SelectItem>
                  </SelectContent>
                </Select>
                {errors.location && touched.location && (
                  <p className="text-sm text-red-500">{errors.location}</p>
                )}
              </div>
              <div className="space-y-4 col-span-2">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Source</label>
                    <Select
                      value={form.source || "none"}
                      onValueChange={value => {
                        const newValue = value === "none" ? "" : value;
                        setForm({ ...form, source: newValue });
                        setTouched({ ...touched, source: true });
                      }}
                    >
                      <SelectTrigger className={errors.source && touched.source ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select Source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select Source</SelectItem>
                        {staticSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.source && touched.source && (
                      <p className="text-sm text-red-500">{errors.source}</p>
                    )}
                  </div>

                  {form.source === 'Other' && (
                    <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                      <label className="text-sm font-medium">Specify Source</label>
                      <Input
                        placeholder="e.g., Naukri, Indeed"
                        value={form.otherSource}
                        onChange={(e) => setForm({ ...form, otherSource: e.target.value })}
                        className={errors.otherSource && touched.otherSource ? 'border-red-500' : ''}
                      />
                      {errors.otherSource && touched.otherSource && (
                        <p className="text-sm text-red-500">{errors.otherSource}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Resume Upload {!isEditMode && <span className="text-red-500">*</span>}</label>
              <div className="space-y-2">
                {!resumeFile && !existingResumeLink ? (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label htmlFor="resume-upload" className="cursor-pointer">
                        <span className="text-sm font-medium text-blue-600 hover:text-blue-500">
                          Click to upload
                        </span>
                        <span className="text-sm text-gray-500"> or drag and drop</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        PDF, DOC, DOCX up to 5MB
                      </p>

                      {isEditMode && candidate?.resumeLink && (
                        <p className="text-xs text-blue-600 mt-2">
                          No resume file selected. Upload a new file to replace the existing resume.
                        </p>
                      )}
                    </div>
                    <input
                      id="resume-upload"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center space-x-3">
                      <Upload className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{resumeFileName}</p>
                        <p className="text-xs text-gray-500">
                          {!resumeFile ? (
                            "Existing resume file"
                          ) : (
                            `${(resumeFile.size / 1024 / 1024).toFixed(2)} MB`
                          )}
                        </p>
                        {isEditMode && candidate?.resumeLink && (
                          <p className="text-xs text-blue-600 mt-1">
                            Upload a new file to replace the existing resume
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeResumeFile}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {(errors.resumeFile || (!resumeFile && !isEditMode && touched.resumeFile)) && (
                  <p className="text-sm text-red-500">
                    {errors.resumeFile || 'Resume file is required for new candidates'}
                  </p>
                )}


                <div className="space-y-2">
                  <label className="text-sm font-medium">Notes</label>
                  <Textarea
                    name="notes"
                    placeholder="Add any internal notes about the candidate"
                    value={form.notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, notes: e.target.value })}
                    className="min-h-[80px]"
                  />
                </div>
              </div>
            </div>






          </form>
        </div>
        <div className="flex-shrink-0 p-6 pt-0">
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="hover:bg-transparent"
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="hover:bg-primary"
              disabled={!isFormValid()}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e as any);
              }}
            >
              {isEditMode ? 'Update Candidate' : 'Add Candidate'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 
